import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import useLoginForm from '@/hooks/useLoginForm';
import { Lock, Mail } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9013cffc-5815-4696-af30-6ef829e3ad5d/artifacts/390zcaea_logo.png";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const onSuccess = useCallback(() => navigate('/admin'), [navigate]);
  const { email, setEmail, password, setPassword, error, loading, submit } = useLoginForm(login, onSuccess);

  return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="SitarMusic" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#fdfbf7] mb-2">Admin Panel</h1>
          <p className="text-[#a3b1c6] text-sm">Sign in to manage your content</p>
        </div>

        <form onSubmit={submit} className="bg-[#122036] border border-[#d4af37]/20 rounded-xl p-8 space-y-5">
          {error && (
            <div data-testid="login-error" className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-3 top-3.5 w-4 h-4 text-[#a3b1c6]/50" />
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="w-full bg-[#0a1628] border border-[#1c2a3d] rounded-md pl-10 pr-4 py-3 text-[#fdfbf7] placeholder:text-[#a3b1c6]/50 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] transition-all outline-none"
              required
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-3.5 w-4 h-4 text-[#a3b1c6]/50" />
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-[#0a1628] border border-[#1c2a3d] rounded-md pl-10 pr-4 py-3 text-[#fdfbf7] placeholder:text-[#a3b1c6]/50 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] transition-all outline-none"
              required
            />
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#d4af37] text-[#0a1628] hover:bg-[#b49129] font-medium py-3 rounded-md transition-all duration-300 disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full text-[#a3b1c6] hover:text-[#d4af37] text-sm py-2 transition-colors"
            data-testid="back-to-home-btn"
          >
            Back to Home
          </button>
        </form>
      </div>
    </div>
  );
}
